/** Активность сотрудников КЦ — ровно то, что отдаёт `GET /api/activity/report`. */
import { apiFetch, query } from "@/lib/api";
import type { UserAction } from "@/lib/dealStage";

/**
 * Уникальные отправки: скрипт CRM, «Чаты» и расширение Chrome; `avito`, `vk`, `ig` — только из
 * «Чатов»; `dups` — отсеянные двойные записи CRM.
 */
/** Аналитика по сообщениям: обращения, которые сотрудник закрыл первым ответом. */
/** Куда ушёл оплачиваемый час: части времени по воронке и их проценты. */
/** Переписка в групповых чатах сотрудников: её не смешиваем с клиентской. */
export type GroupCounts = { msgs: number; chats: number };

export type TimeSplit = {
  in_sec: number;
  out_lead_sec: number;
  out_deal_sec: number;
  out_rest_sec: number;
  chat_sec: number;
  group_sec: number;
  total_sec: number;
  in_pct: number | null;
  out_lead_pct: number | null;
  out_deal_pct: number | null;
  out_rest_pct: number | null;
  chat_pct: number | null;
  group_pct: number | null;
  total_pct: number | null;
};

export type ChatCounts = { contacts: number; chats: number; median_sec: number | null; fast: number };

export type MessengerCounts = {
  sent: number;
  wa: number;
  tg: number;
  max: number;
  avito: number;
  vk: number;
  ig: number;
  dups: number;
};

/**
 * Исходящие по лидам и сделкам: звонков, разговоров (разметка `bitrix_call_talk`, не код 200) и
 * «записали» — после звонка тот же сотрудник перевёл сделку в «Ждем клиента». `pending` —
 * соединились, но разметки ещё нет (свежий день).
 */
export type Dial = {
  lead: number;
  lead_talk: number;
  lead_zapis: number;
  deal: number;
  deal_talk: number;
  deal_zapis: number;
  rest: number;
  rest_talk: number;
  rest_zapis: number;
  talk: number;
  zapis: number;
  pending: number;
};

/**
 * Занятость по действиям (`activity/busy.py`): звонки, сообщения, группы и панель расширения без
 * двойного счёта, в пределах оплачиваемого времени. `pct` — без панели (её видно не у всех),
 * `with_panel_pct` — вместе с ней; `after_last_sec` — оплачиваемое после последнего действия.
 */
export type Active = {
  in_sec: number;
  out_sec: number;
  chat_sec: number;
  group_sec: number;
  panel_sec: number;
  base_sec: number;
  total_sec: number;
  after_last_sec: number;
  pct: number | null;
  with_panel_pct: number | null;
  ext: boolean;
  first_at?: string | null;
  last_at?: string | null;
};

export type Person = {
  user_id: number;
  name: string;
  short: string;
  location: string;
  shift_from: string | null;
  shift_to: string | null;
  paid_min: number;
  line_min: number;
  accept_min: number | null;
  calls: { total: number; out: number; out_ok: number; in: number; other: number };
  dial: Dial;
  /** Дней в отчёте: за период смены «с — по» нет, только их число. */
  days: number;
  missed: number;
  /** Из пропущенных не дошли: на линии и свободен, а телефон и Bitrix не ответили (`call_missed`). */
  unreached?: number;
  talk_sec: number;
  wait_out_sec: number;
  handle_sec: number;
  busy_pct: number | null;
  active: Active;
  per_hour: number | null;
  messenger: MessengerCounts;
  chats: ChatCounts;
  groups: GroupCounts;
  time: TimeSplit;
  /** Простой: оплачиваемое время без своего звонка и без отправки, отрезками ≥ 10 мин. */
  idle_min: number;
  idle_gaps: number;
};

export type Totals = {
  people: number;
  paid_min: number;
  accept_min: number | null;
  calls: number;
  out: number;
  out_ok: number;
  in: number;
  dial: Dial;
  missed: number;
  talk_sec: number;
  handle_sec: number;
  busy_pct: number | null;
  active: Active;
  per_hour: number | null;
  messenger: MessengerCounts;
  chats: ChatCounts;
  groups: GroupCounts;
  idle_min: number;
  time: TimeSplit;
};

export type Line = {
  incoming: number;
  incoming_answered: number;
  incoming_unanswered: number;
  outgoing: number;
  missed_calls: number;
  /** Разных звонков «не дошло» за период. */
  unreached_calls?: number;
};

export type ActivityGroup = "period" | "day";

export type ActivityDay = { day: string; people: Person[]; totals: Totals; line: Line };

export type ActivityReport = {
  day: string;
  from: string;
  to: string;
  group: ActivityGroup;
  user: number | null;
  /** Все сотрудники периода — для выбора, даже когда отобран один. */
  staff: { user_id: number; name: string }[];
  people: Person[];
  totals: Totals;
  line: Line;
  /** С `group=day` — по дням, новые сверху; иначе пусто. */
  days: ActivityDay[];
};

export type ActivityParams = { from: string; to: string; user?: string; group?: ActivityGroup };

export function loadActivity(p: ActivityParams): Promise<ActivityReport> {
  return apiFetch<ActivityReport>(`/api/activity/report${query(p)}`);
}

/** «По дням» — `GET /api/activity/days`: нагрузка, обработка, люди и занятость дня. */
export type CallbackWork = {
  numbers: number;
  called_back: number;
  in_time: number;
  not_called_back: number;
  median: number | null;
  median_raw: number | null;
  p90?: number | null;
  outliers: number;
};

export type DayPerson = {
  user_id: number;
  name: string;
  short: string;
  shift_from: string | null;
  shift_to: string | null;
  paid_min: number;
  line_min: number;
  active: Active;
  busy_pct: number | null;
  calls_in: number;
  calls_out: number;
  dial_talk: number;
  sent: number;
  groups: number;
  missed: number;
  idle_min: number;
};

export type DayRow = {
  day: string;
  people: DayPerson[];
  staff: { people: number; paid_min: number; line_min: number; after_last_min: number };
  calls: {
    numbers: number;
    answered: number;
    calls: number;
    calls_answered: number;
    answered_pct: number | null;
    work: CallbackWork | null;
  };
  chats: {
    in_msgs: number;
    out_msgs: number;
    dialogs: number;
    turns: number;
    turns_answered: number;
    turns_fast: number;
    turns_none: number;
    turn_median_sec: number | null;
    turn_p90_sec: number | null;
    open_at_end: number;
    contacts: number;
    contacts_answered: number;
    contacts_median_sec: number | null;
    contacts_zapis: number;
  };
  outbound: { lead: number; lead_talk: number; lead_zapis: number; deal: number; deal_talk: number; deal_zapis: number };
  load: Active & { must_sec: number; must_pct: number | null; out_pct: number | null; ext_people: number };
  hours: { hour: number; people: number; calls: number; turns: number }[];
};

export type DaysReport = {
  from: string;
  to: string;
  days: DayRow[];
  totals: {
    days: number;
    people_avg: number | null;
    paid_min: number;
    calls_numbers: number;
    calls_answered: number;
    turns: number;
    turns_answered: number;
    turns_fast: number;
    contacts: number;
    callback_median: number | null;
    turn_median_sec: number | null;
    pct: number | null;
    must_pct: number | null;
    out_pct: number | null;
    after_last_min: number;
  };
};

export function loadActivityDays(p: { from: string; to: string }): Promise<DaysReport> {
  return apiFetch<DaysReport>(`/api/activity/days${query(p)}`);
}

/** «По часам» (`GET /api/activity/hours`): входящий вызов часа и почему он пропущен. */
export type HourCall = {
  id: number;
  at: string;
  phone: string;
  answered: boolean;
  wait: number;
  short: boolean;
  reason: "nobody" | "unreached" | "ask" | "other" | null;
  word: string | null;
  who: { name: string; kind: string | null }[];
  callback_at: string | null;
  tried: boolean;
};

export type HourLead = {
  id: number;
  at: string;
  phone: string;
  source: string;
  status: string;
  deadline: string;
  group: "called" | "talked_in" | "no_contact" | "skip";
  waiting: boolean;
  late_at: string | null;
};

/** Счётчики часа и итога дня — одни и те же поля. */
export type HourCounts = {
  incoming: number;
  answered: number;
  missed: number;
  short: number;
  nobody: number;
  unreached: number;
  ask: number;
  outgoing: number;
  leads: number;
  need_call: number;
  called: number;
  talked_in: number;
  no_contact: number;
  overdue: number;
  waiting: number;
  deals: number;
  zapis: number;
};

/** Человек отрезка: смена по графику и статусы по CRM рядом; `note` — чем они расходятся. */
export type HourPerson = {
  name: string;
  plan: string[][];
  statuses: [string, string, string][];
  spans: string[][];
  calls: number;
  sent: number;
  note: string;
};

export type HourRow = HourCounts & {
  start: number;
  end: number;
  future: boolean;
  partial: boolean;
  failure: boolean;
  plan: number;
  line: number;
  people: HourPerson[];
  calls: HourCall[];
  out_calls: { id: number; at: string; phone: string; answered: boolean }[];
  lead_rows: HourLead[];
  deal_rows: { id: number; at: string; title: string }[];
  zapis_rows: { id: number; at: string; title: string }[];
};

export type HoursUsual = { incoming: number; answered: number; leads: number; deals: number; zapis: number };

export type HoursReport = {
  day: string;
  today: boolean;
  now: string;
  totals: HourCounts;
  usual: HoursUsual | null;
  drops: {
    from: number;
    to: number;
    incoming: number;
    answered: number;
    plan: number;
    line_min: number;
    line_max: number;
    people: HourPerson[];
  }[];
  hours: HourRow[];
};

export function loadActivityHours(p: { date: string }): Promise<HoursReport> {
  return apiFetch<HoursReport>(`/api/activity/hours${query(p)}`);
}

/** Отрезок смены: звонок или пропущенный входящий. */
export type CallSpan = {
  id: number;
  kind: "out" | "in" | "other" | "unanswered";
  from: string;
  to: string;
  /** Длительность разговора; у пропущенного — 0. */
  talk_sec: number;
  /** Кому звонили: номер, лид и сделка — для журнала дня. */
  phone: string | null;
  lead_id: number | null;
  deal_id: number | null;
};

export type ShiftStatus = {
  status_id: number;
  name: string;
  paid: boolean;
  line: boolean;
  minutes: number;
  comment: string | null;
  agent: string | null;
  ip: string | null;
  from: string;
  to: string;
};

/** Очередь простоя: максимумы по минутам `dial_queue_minute` и доля записанных минут. */
export type IdleQueue = {
  minutes: number;
  coverage: number;
  max_leads: number;
  max_deals: number;
  max_missed: number;
  max_total: number;
  max_pool_leads: number;
  max_pool_deals: number;
};
/** Были ли задачи во время простоя: work — были, could_take — свои нет, но «Выделить» могло выдать,
 * none — не было, unknown — очередь не записана. */
export type IdleWork = "work" | "could_take" | "none" | "unknown";
export type IdleGap = {
  from: string;
  to: string;
  minutes: number;
  missed: number;
  queue?: IdleQueue | null;
  work?: IdleWork;
};
/** Минута очереди сотрудника: свои задачи (лиды, сделки в «Обзвоне», пропущенные) и пулы «Выделить». */
export type QueueMinute = {
  at: string;
  leads: number;
  deals: number;
  missed: number;
  pool_leads: number;
  pool_deals: number;
};

export type ShiftHour = {
  hour: number;
  out: number;
  out_ok: number;
  in: number;
  missed: number;
  busy_min: number;
  idle_min: number;
  line_min: number;
};

export type Messenger = "wa" | "tg" | "max" | "avito" | "vk" | "ig";

/**
 * Одна уникальная отправка. `source`: `chats` — из раздела «Чаты», `ext` — заметило расширение
 * Chrome (текст — из записи CRM или импортированного диалога; нет — только длина `len`),
 * `crm` — только скрипт CRM. `to` — null, когда получателя никто не записал.
 */
export type MessageSend = {
  id: number | string;
  at: string;
  messenger: Messenger;
  account: string | null;
  to: string | null;
  text: string | null;
  len?: number | null;
  dups: number;
  source: "crm" | "ext" | "chats";
  chat_id: number | null;
  deal_id: number | null;
};

/** Сообщение сотрудника в групповом чате — внутренняя переписка, в `messages` не входит. */
export type GroupSend = {
  id: string;
  at: string;
  messenger: Messenger;
  /** Название группы. */
  to: string | null;
  text: string | null;
  chat_id: number;
};

export type MessengerAccount = {
  messenger: Messenger;
  account: string | null;
  sent: number;
  recipients: number | null;
};

/** Смена сотрудника — ровно то, что отдаёт `GET /api/activity/shift`. */
export type Shift = {
  day: string;
  user_id: number;
  name: string;
  location: string;
  shift_from: string | null;
  shift_to: string | null;
  paid_min: number;
  line_min: number;
  statuses: ShiftStatus[];
  calls: (CallSpan & { talk_sec: number })[];
  missed: CallSpan[];
  idle: IdleGap[];
  hours: ShiftHour[];
  totals: {
    out: number;
    out_ok: number;
    in: number;
    other: number;
    missed: number;
    busy_min: number;
    idle_min: number;
    talk_sec: number;
  };
  /** Журнал `user_action` за день: правки сделки и лида, ссылки, теги — «Чаты» и расширение. */
  actions: UserAction[];
  messages: MessageSend[];
  group_messages: GroupSend[];
  /** Правки сделок и лидов в CRM — одна строка на сделку или лид в минуту (`activity/traces.py`). */
  crm_edits?: CrmEdit[];
  /** События панели расширения, которые делает человек: открыл чат, нашёл номер, клик, ввод. */
  panel?: PanelEvent[];
  /** Очередь по минутам, пока сотрудник в оплачиваемом статусе в КЦ (с 04.11.26). */
  queue?: QueueMinute[];
  /** Акцепт дня (`sUser_time_confirm`) — что принято к оплате. */
  accepted?: { id: number; from: string; to: string; minutes: number; status: number }[];
  /** Оплачиваемое по статусам время вне акцепта — не оплачивается. */
  not_accepted?: { from: string; to: string; minutes: number }[];
  messenger: MessengerCounts;
  messenger_accounts: MessengerAccount[];
};

export type CrmEdit = { at: string; kind: "deal" | "lead"; id: number; fields: string[] };
export type PanelEvent = {
  id: number;
  at: string;
  code: "peer" | "lookup" | "click" | "key" | "insert";
  channel: string;
  name: string | null;
  phone: string | null;
  deal_id: number | null;
  chat_id: number | null;
};

export function loadShift(user: number, day: string): Promise<Shift> {
  return apiFetch<Shift>(`/api/activity/shift${query({ user, date: day })}`);
}

/** Акцепт дня по действиям — `GET /api/activity/accept` (право `activity_control`). */
export type AcceptPeriod = {
  from: string;
  to: string;
  minutes: number;
  activity_start: number | null;
  activity_finish: number | null;
};
export type AcceptIdle = {
  from: string;
  to: string;
  minutes: number;
  missed: number;
  /** Очередь обзвона за простой (`dial_queue_minute`); null — минуты не записаны. */
  queue: IdleQueue | null;
  work: IdleWork;
};
export type AcceptProposal = {
  user_id: number;
  name: string;
  day: string;
  gap_min: number;
  paid_min: number;
  events: number;
  periods: AcceptPeriod[];
  minutes: number;
  idle: AcceptIdle[];
  idle_min: number;
  /** Уже стоящий акцепт CRM за день: периоды и начисление. */
  accepted: {
    minutes: number;
    billed: number | null;
    periods: { id: number; from: string; to: string; minutes: number; category: number }[];
  } | null;
};

export function loadAcceptProposal(user: number, day: string, gap = 20): Promise<AcceptProposal> {
  return apiFetch<AcceptProposal>(`/api/activity/accept${query({ user, date: day, gap })}`);
}

/** «Контроль» — `GET /api/activity/control`: аномалии смены, которые пишет фон `activity_control`. */
export type ControlKind = "line_empty" | "unreached" | "not_answered" | "idle" | "left_early" | "no_show";
export type ControlStatus = "new" | "asked" | "answered" | "closed";

export type ControlIssue = {
  id: number;
  day: string;
  /** `null` — аномалия смены (пустая линия), а не сотрудника. */
  user_id: number | null;
  name: string;
  kind: ControlKind;
  from: string;
  to: string;
  count: number;
  facts: {
    calls?: number[];
    times?: string[];
    messages?: number;
    messages_day?: number;
    accepted?: number;
    plan?: string | null;
    incoming?: number;
    answered?: number;
    who?: string[];
    left_at?: string;
    swap?: string[];
    /** Бездействие: отрезки без следов работы, сумма минут. */
    segments?: { from: string; to: string; minutes: number; missed: number }[];
    minutes?: number;
  };
  text: string;
  question: string;
  status: ControlStatus;
  asked_date: string | null;
  answer: string | null;
  /** «Уведомить»: `null` — не уведомляли; иначе состояние `control_notify`. */
  notify?: IdleNotifyStatus | null;
};

export type IdleNotifyStatus = "crm_done" | "sent" | "send_failed";

/** Строка статуса CRM в предпросмотре «Уведомить»; `new` — вставит «Уведомить». */
export type IdleStatusRow = { id: number | null; new: boolean; status_id: number; name: string; from: string; to: string | null };

/** `GET /api/activity/control/{id}/notify` — что изменится, без записи. */
export type IdleNotifyPreview = {
  issue_id: number;
  blocked: string | null;
  notify: {
    status: IdleNotifyStatus;
    error: string | null;
    minutes: number;
    rows: number[];
    sent_date: string | null;
    date: string | null;
  } | null;
  user_id?: number;
  name?: string;
  day?: string;
  before?: IdleStatusRow[];
  after?: IdleStatusRow[];
  segments?: { from: string; to: string; ok: boolean; reason: string | null }[];
  minutes_before?: number;
  minutes_after?: number;
  fingerprint?: string;
  to?: { tg_id: number; login: string | null } | null;
  text?: string;
  telegram_ok?: boolean;
  telegram_error?: string | null;
};

export type IdleNotifyResult = { issue_id: number; notify: NonNullable<IdleNotifyPreview["notify"]> };

export function loadIdleNotify(id: number): Promise<IdleNotifyPreview> {
  return apiFetch<IdleNotifyPreview>(`/api/activity/control/${id}/notify`);
}

export function sendIdleNotify(id: number, text: string, fingerprint: string): Promise<IdleNotifyResult> {
  return apiFetch<IdleNotifyResult>(`/api/activity/control/${id}/notify`, {
    method: "POST",
    body: JSON.stringify({ text, fingerprint }),
  });
}

export function resendIdleNotify(id: number): Promise<IdleNotifyResult> {
  return apiFetch<IdleNotifyResult>(`/api/activity/control/${id}/notify/resend`, { method: "POST" });
}

export type ControlList = { from: string; to: string; items: ControlIssue[] };
export type ControlAction = "asked" | "answer" | "close" | "reopen";

export function loadControl(from: string, to: string): Promise<ControlList> {
  return apiFetch<ControlList>(`/api/activity/control${query({ from, to })}`);
}

export function markControl(id: number, action: ControlAction, answer?: string): Promise<ControlIssue> {
  return apiFetch<ControlIssue>(`/api/activity/control/${id}`, {
    method: "POST",
    body: JSON.stringify({ action, answer }),
  });
}
